import { useQuery } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { ClientApiFeatureSubPathParams } from "@visioni/a3s-client-api";
import { AssetCameraKey } from "@visioni/a3s-schema";
import { SignedSnapshot } from "../../client-messages/snapshots";
import { snapshotsQuery } from "../queries";

/** Snapshots keyed by canonical `AssetCameraKey` string (JSON-serialisable for query persistence). */
export type SnapshotsByCameraKey = Record<string, SignedSnapshot>;

export type UseSnapshotsOptions = ClientApiFeatureSubPathParams & {
  refetchInterval?: number;
  enabled?: boolean;
};

/**
 * Hook to fetch camera snapshots for the current tenant.
 * Requires ApiConfigProvider to be set up in the component tree.
 */
export function useSnapshots(options: UseSnapshotsOptions) {
  const apiConfig = useApiConfig();
  const { enabled = true, refetchInterval = 0, ...pathParams } = options;

  return useQuery({
    ...snapshotsQuery(apiConfig, pathParams),
    enabled,
    select: (response) => {
      const byCamera: SnapshotsByCameraKey = {};
      for (const item of response.data) {
        byCamera[AssetCameraKey.toString(item.subject.camera)] = item.snapshot;
      }
      return byCamera;
    },
    refetchInterval,
    // Always refetch on mount to ensure fresh thumbnails
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Keep showing previous snapshot URLs while fetching new ones
    placeholderData: (previousData) => previousData,
  });
}
